import type {AmenityConfig} from "./amenity-config";

export function getAmenityAccessLogSheetId(config: AmenityConfig) {
  return `amenity-access-log-${config.type}`;
}

export function isAmenityAccessLogSheetId(sheetId: string, config: AmenityConfig) {
  return sheetId === getAmenityAccessLogSheetId(config);
}

function urlFilesOnly(files: unknown): string[] {
  return Array.isArray(files) ? files.filter((file): file is string => typeof file === "string") : [];
}

function slimProperty(property: unknown) {
  if (!property || typeof property !== "object" || !("id" in property)) {
    return property;
  }
  const p = property as {id: number; name?: string};
  return {id: p.id, name: typeof p.name === "string" ? p.name : ""};
}

function slimHousehold(household: unknown) {
  if (!household || typeof household !== "object" || !("house_hold_id" in household)) {
    return household;
  }
  const h = household as {house_hold_id: number; name?: string};
  return {
    house_hold_id: h.house_hold_id,
    name: typeof h.name === "string" ? h.name : "",
  };
}

function slimAmenity(amenity: unknown) {
  if (!amenity || typeof amenity !== "object" || !("property_amenity_id" in amenity)) {
    return amenity;
  }
  const a = amenity as {property_amenity_id: number; name?: string};
  return {
    property_amenity_id: a.property_amenity_id,
    name: typeof a.name === "string" ? a.name : "",
  };
}

export function getAmenityAccessLogFormDraftKey(
  config: AmenityConfig,
  isEditing: boolean,
  logId?: number | null,
  contextPropertyId?: number | null,
) {
  if (isEditing && logId != null) {
    return `amenity-access-log:${config.type}:edit:${logId}`;
  }
  if (contextPropertyId != null) {
    return `amenity-access-log:${config.type}:create:p${contextPropertyId}`;
  }
  return `amenity-access-log:${config.type}:create`;
}

export function serializeAmenityAccessLogFormDraftValues(values: Record<string, unknown>) {
  return {
    property: slimProperty(values.property),
    house_hold: slimHousehold(values.house_hold),
    property_amenity: slimAmenity(values.property_amenity),
    name: values.name ?? "",
    number_of_guests: values.number_of_guests ?? "",
    key_fob_number: values.key_fob_number ?? "",
    details: values.details ?? "",
    send_notification: values.send_notification ?? false,
    files: urlFilesOnly(values.files),
  };
}

export function deserializeAmenityAccessLogFormDraftValues(values: Record<string, unknown>) {
  return {
    ...values,
    files: Array.isArray(values.files) ? values.files : [],
  };
}

export function amenityAccessLogHasPersistableContent(values: Record<string, unknown>) {
  const name = typeof values.name === "string" ? values.name.trim() : "";
  const details = typeof values.details === "string" ? values.details.trim() : "";
  const keyFob = typeof values.key_fob_number === "string" ? values.key_fob_number.trim() : "";
  const guests = values.number_of_guests;
  const files = Array.isArray(values.files) ? values.files : [];
  return (
    name.length > 0 ||
    details.length > 0 ||
    keyFob.length > 0 ||
    (guests !== "" && guests != null) ||
    files.length > 0 ||
    values.house_hold != null ||
    values.property_amenity != null ||
    values.send_notification === true
  );
}
